import Grid from '@mui/material/Grid'
import TopBar from '../../../../../components/TopBar/TopBar';
import { useRouter } from 'next/router';
import Div100vh from 'react-div-100vh';
import { ProductName } from '../../../../../components/ProductName/ProductName';
import { getAllVariantModels, getVariantModelBySlug, useVariantModelBySlug } from '../../../../../shared/query-hooks/VariantModelService';
import LoadingView from '../../../../../components/Loading/LoadingView';
import CustomModelViewerView from '../../../../../components/CustomModelViewerView/CustomModelViewerView';
import Head from 'next/head';
import { GetServerSidePropsContext, GetStaticPaths, NextPage } from 'next/types';
import { VariantModel } from '../../../../../shared/utils/interfaces/VariantModelResponse';
import { isExternalVisit } from '../../../../../shared/route';
import Dynamic100Vh from '../../../../../components/DynamicDiv100vh/Div100Vh';
import ThreeDModelMetaScript from '../../../../../components/Schema/ThreeDModelMetaScript';
import { logNetworkError } from '../../../../../assets/scripts/requestHelper';
interface SingleProductPageProps {
    initialSingleProductViewData: VariantModel,
    ssrCookies: string,
    ssrCookie: string,
}

const SingleProductView: NextPage<SingleProductPageProps> = ({ initialSingleProductViewData, ssrCookies, ssrCookie }) => {
    let router = useRouter();
    const { category, brandId, baseModelId, variantModelSlug } = router.query;

    const { data, status } = useVariantModelBySlug(variantModelSlug! as string, baseModelId! as string, {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        initialData: initialSingleProductViewData
    });

    return (
        <>
            {status === "success" ?
                <>
                    <Head>
                        <title>{data?.data[0].brand_detail[0].brand_name!} - {data?.data[0].base_model_detail[0].model_name!} - {data?.data[0].variant_model_name!}</title>
                        <meta name="description" content={data?.data[0].seo_description || `Online shopping just got easier. Virtually try on ${data?.data[0].brand_detail[0].brand_name!} ${data?.data[0].base_model_detail[0].model_name!} ${data?.data[0].variant_model_name!} before buying with TRYO.`} />
                        <meta name="keywords" content={`${data?.data[0].brand_detail[0].brand_name!}, ${data?.data[0].base_model_detail[0].model_name!}, ${data?.data[0].variant_model_name!}, augmented reality, AR, vto, virtual,try,tryon`} />
                        <meta property='og:title' content={`${data?.data[0].brand_detail[0].brand_name!} - ${data?.data[0].base_model_detail[0].model_name!} - ${data?.data[0].variant_model_name!}`} />
                        <meta property='og:description' content={`${data?.data[0].brand_detail[0].brand_name!} - ${data?.data[0].base_model_detail[0].model_name!} - ${data?.data[0].variant_model_name!}`} />
                        <meta property="og:image" content={`${data?.data[0].thumbnail_file[0].url}`} />
                        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_DOMAIN}/${category}/${brandId}/${baseModelId}/${variantModelSlug}`} />
                        <ThreeDModelMetaScript
                            model={initialSingleProductViewData.data[0].glb_file[0].url}
                            name={`${initialSingleProductViewData.data[0]?.brand_detail[0].brand_name} ${initialSingleProductViewData.data[0]?.base_model_detail[0].model_name} ${initialSingleProductViewData.data[0]?.variant_model_name}`}
                            image={initialSingleProductViewData.data[0].thumbnail_file[0].url}
                            format="GLB"
                        />
                        {initialSingleProductViewData.data[0]?.usdz_file &&
                            <ThreeDModelMetaScript
                                model={initialSingleProductViewData.data[0].usdz_file[0].url}
                                name={`${initialSingleProductViewData.data[0]?.brand_detail[0].brand_name} ${initialSingleProductViewData.data[0]?.base_model_detail[0].model_name} ${initialSingleProductViewData.data[0]?.variant_model_name}`}
                                image={initialSingleProductViewData.data[0].thumbnail_file[0].url}
                                format="USDZ"
                            />}
                    </Head>
                    <Dynamic100Vh style={{ width: "100%" }}>
                        <Grid container direction={"column"} height={"100%"}>
                            <Grid item>
                                <TopBar
                                    destination={isExternalVisit() ? `/` : ``}
                                    textComponent={<ProductName brandName={data?.data[0].brand_detail[0].brand_name!}
                                        baseModelName={data?.data[0].base_model_detail[0].model_name!}
                                        variantModelName={data?.data[0].variant_model_name!} />}></TopBar>
                            </Grid>
                            <Grid item container flexGrow={1} pt={"4px"}>
                                <CustomModelViewerView single selectedVariantModel={data?.data[0]!} />
                            </Grid>
                        </Grid>
                    </Dynamic100Vh>

                </>
                :
                <Div100vh style={{ width: "100%" }}>
                    <LoadingView />
                </Div100vh>
            }
        </>
    )
}

// SSR Query
export async function getStaticProps(context: GetServerSidePropsContext) {
    try {
        // Prepare data for initial render
        console.log('received ssr single base model request');
        const initialSingleProductViewData = await getVariantModelBySlug(context.params?.variantModelSlug as string, context.params?.baseModelId as string, process.env.SSR_TOKEN);

        return {
            props: {
                initialSingleProductViewData
            },
            revalidate: 10
        }
    } catch (e: any) {
        logNetworkError(e);
        return { notFound: true };
    }
}

export const getStaticPaths: GetStaticPaths = async () => {
    // To skip prerender all dynamic routes add SKIP_BUILD_STATIC_GENERATION=true to .env
    if (process.env.SKIP_BUILD_STATIC_GENERATION) {
        return {
            paths: [],
            fallback: 'blocking',
        }
    }

    const variants = await getAllVariantModels(process.env.SSR_TOKEN);

    return {
        paths: [
            ...variants.data.map(variant => ({ params: { category: variant.category.toLowerCase(), brandId: variant.brand_detail[0].brand_slug, baseModelId: variant.base_model_detail[0].model_slug, variantModelSlug: variant.variant_model_slug } })),
        ], //indicates that no page needs be created at build time
        fallback: 'blocking' //indicates the type of fallback
    }
}

export default SingleProductView